// Types are defined locally to avoid importing from pc-api (which pulls in
// browser-only deps like window.location). These mirror the shapes in pc-api.ts.

export interface DutyColumn {
  id: string;
  name: string;
  displayOrder: number;
}

export interface AdditionalTariff {
  columnId: string;
  columnName: string;
  rate: number;
}

export interface ProductBreakdownLine {
  lineType: string;
  portion: number;
  applicableRules: AdditionalTariff[];
  totalRate: number;
}

export interface CsvProduct {
  sku: string;
  name: string;
  description?: string;
  htsCode: string;
  countryOfOrigin: string;
  metalPercentage?: number | null;
  additionalTariffs: AdditionalTariff[];
  totalDutyRate?: number;
  breakdownLines?: ProductBreakdownLine[];
  riskAnalysis?: { riskLevel: string } | null;
}

export const LINE_TYPE = {
  METAL: "METAL",
  NONMETAL: "NONMETAL",
  FULL: "FULL",
  STEEL: "STEEL",
  ALUMINUM: "ALUMINUM",
  COPPER: "COPPER",
} as const;

export function buildProductCsv(
  allProducts: CsvProduct[],
  dutyColumns: DutyColumn[]
): string {
  if (allProducts.length === 0) return "";

  const shouldIncludeMetalPercentage = allProducts.some(
    (product) =>
      product.metalPercentage !== null &&
      product.metalPercentage !== undefined &&
      product.metalPercentage < 100
  );

  const hasAnyMultiLine = allProducts.some(
    (product) =>
      product.breakdownLines &&
      product.breakdownLines.filter((l) => l.portion > 0).length > 1
  );

  const headers = [
    "SKU",
    "Name",
    "Description",
    "HTS Code",
    "AI Risk Score",
    "Country of Origin",
  ];
  if (shouldIncludeMetalPercentage) {
    headers.push("Metal Percentage");
  }
  if (hasAnyMultiLine) {
    headers.push("Line Type", "Portion");
  }

  const getLineLabel = (lineType: string): string => {
    switch (lineType) {
      case LINE_TYPE.STEEL:
        return "Steel";
      case LINE_TYPE.ALUMINUM:
        return "Aluminum";
      case LINE_TYPE.COPPER:
        return "Copper";
      case LINE_TYPE.METAL:
        return "Metal";
      case LINE_TYPE.NONMETAL:
        return "Non-Metal";
      case LINE_TYPE.FULL:
      default:
        return "Full";
    }
  };

  // Discover tariff columns
  const exportTariffColumns = (() => {
    const dutyColumnIds = new Set(dutyColumns.map((col) => col.id));
    const generalDutyColumns: DutyColumn[] = [];
    const unmatchedColumns = new Map<string, string>();

    allProducts.forEach((product: CsvProduct) => {
      product.additionalTariffs.forEach((tariff: any) => {
        if (!dutyColumnIds.has(tariff.columnId)) {
          if (
            tariff.columnName.toLowerCase().includes("general duty") ||
            tariff.columnName === "General Duty Rate" ||
            !tariff.columnId
          ) {
            if (!generalDutyColumns.find((col) => col.id === tariff.columnId)) {
              generalDutyColumns.push({
                id: tariff.columnId || "general-duty",
                name: tariff.columnName,
                displayOrder: -1,
              });
            }
          } else {
            if (!unmatchedColumns.has(tariff.columnId)) {
              unmatchedColumns.set(tariff.columnId, tariff.columnName);
            }
          }
        }
      });

      product.breakdownLines?.forEach((line) => {
        line.applicableRules.forEach((rule: any) => {
          if (
            !dutyColumnIds.has(rule.columnId) &&
            !generalDutyColumns.find((col) => col.id === rule.columnId) &&
            !unmatchedColumns.has(rule.columnId)
          ) {
            if (
              rule.columnName?.toLowerCase().includes("general duty") ||
              rule.columnName === "General Duty Rate" ||
              !rule.columnId
            ) {
              if (
                !generalDutyColumns.find(
                  (col) => col.id === (rule.columnId || "general-duty")
                )
              ) {
                generalDutyColumns.push({
                  id: rule.columnId || "general-duty",
                  name: rule.columnName,
                  displayOrder: -1,
                });
              }
            } else {
              unmatchedColumns.set(rule.columnId, rule.columnName);
            }
          }
        });
      });
    });

    const allCols = [
      ...generalDutyColumns,
      ...dutyColumns,
      ...Array.from(unmatchedColumns.entries()).map(([id, name]) => ({
        id,
        name,
        displayOrder: 999,
      })),
    ];

    return allCols.filter((col) => {
      return allProducts.some((product: CsvProduct) => {
        const tariff = product.additionalTariffs.find(
          (t: any) =>
            t.columnId === col.id || (!t.columnId && col.id === "general-duty")
        );
        if (tariff && tariff.rate > 0) return true;
        return (
          product.breakdownLines?.some((line) =>
            line.applicableRules.some(
              (r: any) =>
                (r.columnId === col.id ||
                  (!r.columnId && col.id === "general-duty")) &&
                r.rate > 0
            )
          ) || false
        );
      });
    });
  })();

  exportTariffColumns.forEach((col) => headers.push(col.name));
  headers.push("Line Total");
  if (hasAnyMultiLine) {
    headers.push("Product Total");
  }

  // Build CSV rows
  const csvData: string[][] = [];
  allProducts.forEach((row: CsvProduct) => {
    const activeLines =
      row.breakdownLines?.filter((l) => l.portion > 0) || [];
    const hasMultiLine = activeLines.length > 1;
    const productTotal =
      row.totalDutyRate !== undefined
        ? row.totalDutyRate
        : row.additionalTariffs.reduce((sum, t) => sum + t.rate, 0);

    if (hasMultiLine) {
      activeLines.forEach((line) => {
        const rowData = [
          row.sku,
          row.name,
          row.description || "",
          row.htsCode,
          row.riskAnalysis?.riskLevel || "N/A",
          row.countryOfOrigin,
        ];

        if (shouldIncludeMetalPercentage) {
          rowData.push(
            row.metalPercentage !== null && row.metalPercentage !== undefined
              ? `${row.metalPercentage}%`
              : "N/A"
          );
        }

        rowData.push(getLineLabel(line.lineType));
        rowData.push(`${(line.portion * 100).toFixed(0)}%`);

        exportTariffColumns.forEach((col: any) => {
          const rule = line.applicableRules.find(
            (r: any) =>
              r.columnId === col.id ||
              (!r.columnId && col.id === "general-duty")
          );
          rowData.push(rule ? `${(rule.rate * 100).toFixed(2)}%` : "0%");
        });

        rowData.push(`${(line.totalRate * 100).toFixed(2)}%`);
        rowData.push(`${(productTotal * 100).toFixed(2)}%`);

        csvData.push(rowData);
      });
    } else {
      const rowData = [
        row.sku,
        row.name,
        row.description || "",
        row.htsCode,
        row.riskAnalysis?.riskLevel || "N/A",
        row.countryOfOrigin,
      ];

      if (shouldIncludeMetalPercentage) {
        rowData.push(
          row.metalPercentage !== null && row.metalPercentage !== undefined
            ? `${row.metalPercentage}%`
            : "N/A"
        );
      }

      if (hasAnyMultiLine) {
        rowData.push("Full", "100%");
      }

      exportTariffColumns.forEach((col: any) => {
        const colRate = row.additionalTariffs
          .filter(
            (t: any) =>
              t.columnId === col.id ||
              (!t.columnId && col.id === "general-duty")
          )
          .reduce((sum: number, t: any) => sum + t.rate, 0);
        rowData.push(colRate > 0 ? `${(colRate * 100).toFixed(2)}%` : "0%");
      });

      rowData.push(`${(productTotal * 100).toFixed(2)}%`);

      if (hasAnyMultiLine) {
        rowData.push(`${(productTotal * 100).toFixed(2)}%`);
      }

      csvData.push(rowData);
    }
  });

  // SKU is column 0; use ="value" formula to force Excel text format
  // so numeric SKUs aren't truncated or shown in scientific notation
  const csvContent = [
    headers.map((header) => `"${header}"`).join(","),
    ...csvData.map((row) =>
      row
        .map((cell, colIdx) => {
          if (colIdx === 0) return `"=""${cell}"""`;
          return `"${cell}"`;
        })
        .join(",")
    ),
  ].join("\n");

  return csvContent;
}
